import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button, Row, Col, DatePicker, Space, Table, Collapse, Modal, Form, Input, Select, InputNumber, Tooltip, message } from 'antd';
import { ArrowLeftOutlined, EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import EditHostelModal from '../components/EditHostelModal';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;

const HostelDetail = () => {
  const { id } = useParams();
  const [hostel, setHostel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRoomModal, setShowRoomModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [dateRange, setDateRange] = useState([dayjs(), dayjs().add(7, 'day')]);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  useEffect(() => {
    fetchHostel();
  }, [id]);

  const fetchHostel = async () => {
    try {
      const response = await api.get(`/hostels/${id}`);
      setHostel(response.data);
    } catch (error) {
      message.error('Failed to load hostel');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailability = async () => {
    if (!dateRange?.[0] || !dateRange?.[1] || !dayjs(dateRange[1]).isAfter(dayjs(dateRange[0]))) {
      message.warning('Select valid check-in and check-out dates.');
      return;
    }
    setAvailabilityLoading(true);
    try {
      const res = await api.get(`/hostels/${id}/availability`, {
        params: {
          checkIn: dateRange[0].format('YYYY-MM-DD'),
          checkOut: dateRange[1].format('YYYY-MM-DD'),
        },
      });
      setAvailability(res.data);
    } catch (err) {
      setAvailability(null);
      message.error(err.response?.data?.message || 'Failed to load availability');
    } finally {
      setAvailabilityLoading(false);
    }
  };

  const handleDelete = () => {
    Modal.confirm({
      title: `Delete "${hostel?.name}"?`,
      content: 'This will remove the hostel and cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await api.delete(`/hostels/${id}`);
          message.success('Hostel deleted');
          navigate('/hostels');
        } catch (error) {
          message.error(error.response?.data?.message || 'Delete failed');
        }
      },
    });
  };

  const handleAddRoom = async () => {
    try {
      const values = await form.validateFields();
      await api.post(`/hostels/${id}/rooms`, {
        roomNumber: values.roomNumber,
        roomType: values.roomType,
        capacity: values.capacity,
        description: values.description,
      });
      message.success('Room added');
      form.resetFields();
      setShowRoomModal(false);
      fetchHostel();
    } catch (err) {
      if (err.errorFields) return;
      message.error(err.response?.data?.message || 'Failed to add room');
    }
  };

  if (loading || !hostel) return null;
  if (!hostel) return null;

  const canManage = user?.role === 'owner' || user?.role === 'employee';
  const availableRooms = availability?.rooms?.filter((r) => r.availableCount > 0) || [];

  return (
    <>
      <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/hostels')} style={{ marginBottom: 16 }}>
        Back to Hostels
      </Button>

      <Card
        title={hostel.name}
        extra={
          user?.role === 'owner' && (
            <Space>
              <Tooltip title="Edit Hostel">
                <Button icon={<EditOutlined />} onClick={() => setShowEditModal(true)} />
              </Tooltip>
              <Tooltip title="Delete Hostel">
                <Button danger icon={<DeleteOutlined />} onClick={handleDelete} />
              </Tooltip>
            </Space>
          )
        }
      >
        <p><strong>Address:</strong> {hostel.address?.street}, {hostel.address?.city}, {hostel.address?.country}</p>
        {hostel.address?.postalCode && <p><strong>Postal Code:</strong> {hostel.address.postalCode}</p>}
        {hostel.description && <p>{hostel.description}</p>}
      </Card>

      {canManage && (
        <Card title="Room availability" style={{ marginTop: 24 }}>
          <p style={{ color: '#666', marginBottom: 12 }}>Check which rooms and beds are available for a date range.</p>
          <Space wrap style={{ marginBottom: 16 }}>
            <RangePicker value={dateRange} onChange={setDateRange} />
            <Button type="primary" onClick={fetchAvailability} loading={availabilityLoading}>
              Show available rooms
            </Button>
          </Space>
          {availability && (
            <div style={{ marginTop: 16 }}>
              <p><strong>Requested period:</strong> {dayjs(availability.checkIn).format('L')} – {dayjs(availability.checkOut).format('L')}</p>
              {availableRooms.length === 0 ? (
                <p style={{ color: '#666' }}>No rooms available for this period.</p>
              ) : (
                <Row gutter={[16, 16]} style={{ marginTop: 12 }}>
                  {availableRooms.map((room) => (
                    <Col xs={24} md={12} key={room._id}>
                      <Card size="small" title={`Room ${room.roomNumber} (${room.roomType})`}>
                        <p>{room.availableCount} bed(s) available</p>
                        {room.availableRanges?.length > 0 && (
                          <p style={{ fontSize: 13, color: '#333' }}>
                            Available: {room.availableRanges.map((r) => `${dayjs(r.start).format('L')} – ${dayjs(r.end).format('L')}`).join(', ')}
                          </p>
                        )}
                        {room.beds?.filter((b) => b.available).length > 0 && (
                          <Collapse size="small" items={[{ key: '1', label: 'Beds', children: <ul style={{ margin: 0, paddingLeft: 20 }}>{room.beds.filter((b) => b.available).map((b) => <li key={b._id}>Bed {b.bedNumber}</li>)}</ul> }]} />
                        )}
                      </Card>
                    </Col>
                  ))}
                </Row>
              )}
            </div>
          )}
        </Card>
      )}

      <Card
        title="Rooms"
        extra={
          canManage && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setShowRoomModal(true)}>
              Add Room
            </Button>
          )
        }
        style={{ marginTop: 24 }}
      >
        {!hostel.rooms?.length ? (
          <p>No rooms yet.</p>
        ) : (
          <Row gutter={[16, 16]}>
            {hostel.rooms.map((room, idx) => (
              <Col xs={24} sm={12} md={8} key={idx}>
                <Card size="small">
                  <p><strong>Room {room.roomNumber}</strong></p>
                  <p>Type: {room.roomType}</p>
                  <p>Capacity: {room.capacity}</p>
                  <p>Beds: {room.beds?.length || 0}</p>
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </Card>

      <Modal
        title="Add Room"
        open={showRoomModal}
        onCancel={() => { setShowRoomModal(false); form.resetFields(); }}
        onOk={handleAddRoom}
        destroyOnClose
        okText="Add"
      >
        <Form form={form} layout="vertical">
          <Form.Item name="roomNumber" label="Room Number" rules={[{ required: true }]}>
            <Input placeholder="e.g. 101" />
          </Form.Item>
          <Form.Item name="roomType" label="Room Type" initialValue="dorm" rules={[{ required: true }]}>
            <Select options={[{ value: 'dorm', label: 'Dorm' }, { value: 'private', label: 'Private' }]} />
          </Form.Item>
          <Form.Item name="capacity" label="Capacity" initialValue={4} rules={[{ required: true }]}>
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} placeholder="Optional" />
          </Form.Item>
        </Form>
      </Modal>

      {showEditModal && hostel && (
        <EditHostelModal hostel={hostel} onClose={() => setShowEditModal(false)} onSuccess={() => { setShowEditModal(false); fetchHostel(); }} />
      )}
    </>
  );
};

export default HostelDetail;
